'use client';

/**
 * demoStore/index.ts — Centralised persistent demo data layer.
 *
 * Architecture:
 *   - Single localStorage key: plenaricat_demo_state_v1
 *   - Immutable updates: all mutations return new state objects
 *   - First load: mocks → createInitialDemoState() → validateDemoState() → saveDemoState()
 *   - Subsequent loads: localStorage → migrateDemoState() → validateDemoState() → app
 *   - Components MUST NOT access localStorage, mocks, or arrays directly
 *
 * Lifecycle policy (Issue):
 *   - archivedAt: lifecycle timestamp for archive
 *   - status: functional state of the issue
 *   - statusBeforeArchive: preserved so restoreIssue can recover the previous status
 *   - Forbidden after migration: status === 'arxivat' && archivedAt === null
 */

import type { DemoState, DemoIssue, DemoNotification, DemoWorkspace } from './schema';
import type { PlenarySession, AgendaItem, Document, Draft, SuggestedQuestion } from '@/types/models';
import { readRawState, writeRawState, clearRawState, backupCurrentState, removeLegacyKeys } from './storage';
import { createInitialDemoState } from './initialState';
import { migrateDemoState } from './migrations';
import { validateDemoState } from './validation';

// ─── Hydration guard ───────────────────────────────────────────────────

let _hydrated = false;
let _state: DemoState | null = null;
const _subscribers: Array<(state: DemoState) => void> = [];

export function isDemoStoreHydrated(): boolean {
  return _hydrated;
}

// ─── Core API ──────────────────────────────────────────────────────────

export function getDemoState(): DemoState {
  if (!_hydrated || !_state) {
    throw new Error('DemoStore not hydrated. Call initializeDemoState() first.');
  }
  return _state;
}

export function saveDemoState(state: DemoState): void {
  if (typeof window === 'undefined') return;
  let updated: DemoState = {
    ...state,
    metadata: {
      ...state.metadata,
      lastModifiedAt: new Date().toISOString(),
    },
  };
  _state = updated;
  writeRawState(updated);
  _notifySubscribers(updated);
}

export function updateDemoState(updater: (state: DemoState) => DemoState): DemoState {
  const current = getDemoState();
  const next = updater(current);
  saveDemoState(next);
  return next;
}

export function initializeDemoState(): DemoState {
  if (typeof window === 'undefined') {
    // SSR: return seed without persisting
    return createInitialDemoState();
  }

  const raw = readRawState();

  if (raw === null) {
    // First load: build from mocks
    const initial = createInitialDemoState();
    const errors = validateDemoState(initial);
    if (errors.length > 0) {
      console.warn('[demoStore] Initial state validation warnings:', errors);
    }
    writeRawState(initial);
    // Clean up any legacy keys
    removeLegacyKeys();
    _state = initial;
    _hydrated = true;
    return initial;
  }

  // Subsequent loads: migrate and validate
  const migrated = migrateDemoState(raw);
  const errors = validateDemoState(migrated);
  if (errors.length > 0) {
    console.warn('[demoStore] State validation warnings after migration:', errors);
  }

  // Clean up legacy keys if they exist
  removeLegacyKeys();

  writeRawState(migrated);
  _state = migrated;
  _hydrated = true;
  return migrated;
}

export function resetDemoState(): DemoState {
  if (typeof window === 'undefined') return createInitialDemoState();
  // Backup before reset
  backupCurrentState();
  clearRawState();
  const fresh = createInitialDemoState();
  writeRawState(fresh);
  _state = fresh;
  _notifySubscribers(fresh);
  return fresh;
}

export function validateDemoStatePublic(): ReturnType<typeof validateDemoState> {
  if (!_state) return [{ field: 'root', message: 'State not initialized' }];
  return validateDemoState(_state);
}

// ─── Subscriptions ─────────────────────────────────────────────────────

export function subscribeDemoState(cb: (state: DemoState) => void): () => void {
  _subscribers.push(cb);
  return () => {
    const idx = _subscribers.indexOf(cb);
    if (idx !== -1) _subscribers.splice(idx, 1);
  };
}

function _notifySubscribers(state: DemoState): void {
  _subscribers.forEach((cb) => {
    try { cb(state); } catch { /* ignore subscriber errors */ }
  });
}

// ─── Plenary Sessions ──────────────────────────────────────────────────

export function getSessions(): PlenarySession[] {
  if (!_hydrated || !_state) return [];
  return _state.plenarySessions;
}

export function saveSessions(sessions: PlenarySession[]): void {
  updateDemoState((s) => ({ ...s, plenarySessions: sessions }));
}

export function addSession(session: PlenarySession): void {
  updateDemoState((s) => ({ ...s, plenarySessions: [session, ...s.plenarySessions] }));
}

export function updateSession(id: string, updates: Partial<PlenarySession>): void {
  updateDemoState((s) => ({
    ...s,
    plenarySessions: s.plenarySessions.map((sess) =>
      sess.id === id ? { ...sess, ...updates } : sess
    ),
  }));
}

// ─── Agenda Items ──────────────────────────────────────────────────────

export function getAgendaItems(): AgendaItem[] {
  if (!_hydrated || !_state) return [];
  return _state.agendaItems;
}

export function saveAgendaItems(items: AgendaItem[]): void {
  updateDemoState((s) => ({ ...s, agendaItems: items }));
}

export function addAgendaItem(item: AgendaItem): void {
  updateDemoState((s) => ({ ...s, agendaItems: [...s.agendaItems, item] }));
}

export function updateAgendaItem(id: string, updates: Partial<AgendaItem>): AgendaItem | null {
  let updated: AgendaItem | null = null;
  updateDemoState((s) => {
    const items = s.agendaItems.map((i) => {
      if (i.id === id) {
        updated = { ...i, ...updates };
        return updated;
      }
      return i;
    });
    return { ...s, agendaItems: items };
  });
  return updated;
}

export function getAgendaItemsBySession(plenarySessionId: string): AgendaItem[] {
  return getAgendaItems().filter((i) => i.plenarySessionId === plenarySessionId);
}

// ─── Documents ─────────────────────────────────────────────────────────

export function getDocuments(): Document[] {
  if (!_hydrated || !_state) return [];
  return _state.documents;
}

export function saveDocuments(docs: Document[]): void {
  updateDemoState((s) => ({ ...s, documents: docs }));
}

export function addDocument(doc: Document): void {
  updateDemoState((s) => ({ ...s, documents: [doc, ...s.documents] }));
}

export function getDocumentsBySession(plenarySessionId: string): Document[] {
  return getDocuments().filter((d) => d.plenarySessionId === plenarySessionId);
}

// ─── Drafts ────────────────────────────────────────────────────────────

export function getDraftsStore(): Draft[] {
  if (!_hydrated || !_state) return [];
  return _state.drafts;
}

export function saveDraftsStore(drafts: Draft[]): void {
  updateDemoState((s) => ({ ...s, drafts }));
}

export function addDraftStore(draft: Draft): void {
  updateDemoState((s) => ({ ...s, drafts: [draft, ...s.drafts] }));
}

export function updateDraftStore(id: string, updates: Partial<Draft>): Draft | null {
  let updated: Draft | null = null;
  updateDemoState((s) => {
    const drafts = s.drafts.map((d) => {
      if (d.id === id) {
        updated = {
          ...d,
          ...updates,
          dataModificacio: new Date().toISOString().split('T')[0],
        };
        return updated;
      }
      return d;
    });
    return { ...s, drafts };
  });
  return updated;
}

export function getDraftsByMunicipality(municipiId: string): Draft[] {
  return getDraftsStore().filter((d) => d.municipiId === municipiId);
}

/** Active drafts: excludes trashed. */
export function getDrafts(municipiId: string): Draft[] {
  return getDraftsStore().filter((d) => d.municipiId === municipiId && !d.deletedAt);
}

/** Drafts in the trash. */
export function getTrashedDrafts(municipiId: string): Draft[] {
  return getDraftsStore().filter((d) => d.municipiId === municipiId && !!d.deletedAt);
}

// ─── Issues ────────────────────────────────────────────────────────────

export function getIssuesStore(): DemoIssue[] {
  if (!_hydrated || !_state) return [];
  return _state.issues;
}

export function saveIssuesStore(issues: DemoIssue[]): void {
  updateDemoState((s) => ({ ...s, issues }));
}

export function addIssueStore(issue: DemoIssue): void {
  updateDemoState((s) => ({ ...s, issues: [issue, ...s.issues] }));
}

export function updateIssueStore(id: string, updates: Partial<DemoIssue>): DemoIssue | null {
  let updated: DemoIssue | null = null;
  updateDemoState((s) => {
    const issues = s.issues.map((i) => {
      if (i.id === id) {
        updated = { ...i, ...updates };
        return updated;
      }
      return i;
    });
    return { ...s, issues };
  });
  return updated;
}

export function getIssuesByMunicipality(municipiId: string): DemoIssue[] {
  return getIssuesStore().filter((i) => i.municipiId === municipiId);
}

/** Active issues: excludes trashed. */
export function getIssues(municipiId: string): DemoIssue[] {
  return getIssuesStore().filter((i) => i.municipiId === municipiId && !i.deletedAt);
}

/** Issues in the trash. */
export function getTrashedIssues(municipiId: string): DemoIssue[] {
  return getIssuesStore().filter((i) => i.municipiId === municipiId && !!i.deletedAt);
}

// ─── Suggested Questions ───────────────────────────────────────────────

export function getSuggestedQuestions(): SuggestedQuestion[] {
  if (!_hydrated || !_state) return [];
  return _state.suggestedQuestions;
}

export function saveSuggestedQuestions(questions: SuggestedQuestion[]): void {
  updateDemoState((s) => ({ ...s, suggestedQuestions: questions }));
}

export function markQuestionConverted(id: string, draftId: string): void {
  updateDemoState((s) => ({
    ...s,
    suggestedQuestions: s.suggestedQuestions.map((q) =>
      q.id === id ? { ...q, convertitEnEsborrany: true, draftId } : q
    ),
  }));
}

export function getSuggestedQuestionsByItem(agendaItemId: string): SuggestedQuestion[] {
  return getSuggestedQuestions().filter((q) => q.agendaItemId === agendaItemId);
}

export function dismissSuggestedQuestion(id: string): void {
  updateDemoState((s) => ({
    ...s,
    suggestedQuestions: s.suggestedQuestions.map((q) =>
      q.id === id ? { ...q, dismissedAt: new Date().toISOString() } : q
    ),
  }));
}

export function restoreSuggestedQuestion(id: string): void {
  updateDemoState((s) => ({
    ...s,
    suggestedQuestions: s.suggestedQuestions.map((q) =>
      q.id === id ? { ...q, dismissedAt: null } : q
    ),
  }));
}

// ─── Item Notes ────────────────────────────────────────────────────────

export function getItemNotes(): Record<string, string[]> {
  if (!_hydrated || !_state) return {};
  return _state.internalNotes;
}

export function addItemNote(agendaItemId: string, note: string): void {
  updateDemoState((s) => ({
    ...s,
    internalNotes: {
      ...s.internalNotes,
      [agendaItemId]: [...(s.internalNotes[agendaItemId] ?? []), note],
    },
  }));
}

export function getNotesForItem(agendaItemId: string): string[] {
  return getItemNotes()[agendaItemId] ?? [];
}

// ─── Item Responsables ─────────────────────────────────────────────────

export function getItemResponsables(): Record<string, string> {
  if (!_hydrated || !_state) return {};
  return _state.itemResponsables;
}

export function setItemResponsable(agendaItemId: string, responsable: string): void {
  updateDemoState((s) => ({
    ...s,
    itemResponsables: { ...s.itemResponsables, [agendaItemId]: responsable },
  }));
}

// ─── Notifications ─────────────────────────────────────────────────────

export function getNotifications(municipalityId?: string): DemoNotification[] {
  if (!_hydrated || !_state) return [];
  if (!municipalityId) return _state.notifications;
  return _state.notifications.filter((n) => n.municipalityId === municipalityId);
}

export function markNotificationRead(id: string): void {
  updateDemoState((s) => ({
    ...s,
    notifications: s.notifications.map((n) =>
      n.id === id ? { ...n, llegit: true } : n
    ),
    alerts: s.alerts.map((a) =>
      a.id === id ? { ...a, llegit: true } : a
    ),
  }));
}

export function markAllNotificationsRead(municipalityId: string): void {
  updateDemoState((s) => ({
    ...s,
    notifications: s.notifications.map((n) =>
      n.municipalityId === municipalityId ? { ...n, llegit: true } : n
    ),
    alerts: s.alerts.map((a) =>
      a.municipalityId === municipalityId ? { ...a, llegit: true } : a
    ),
  }));
}

// ─── Alerts (legacy compat) ────────────────────────────────────────────

/**
 * Returns alerts filtered by municipalityId.
 * When workspaceId is provided, further filters by workspace (primary boundary).
 * Falls back to municipalityId-only filter for legacy alerts without workspaceId.
 */
export function getAlerts(municipalityId?: string, workspaceId?: string | null): DemoNotification[] {
  if (!_hydrated || !_state) return [];
  if (!municipalityId) return _state.alerts;
  if (workspaceId) {
    return _state.alerts.filter(
      (a) => a.workspaceId === workspaceId || (!a.workspaceId && a.municipalityId === municipalityId)
    );
  }
  return _state.alerts.filter((a) => a.municipalityId === municipalityId);
}

// ─── Preferences ──────────────────────────────────────────────────────

export function getPreferences() {
  if (!_hydrated || !_state) return null;
  return _state.preferences;
}

export function savePreferences(prefs: Partial<DemoState['preferences']>): void {
  updateDemoState((s) => ({
    ...s,
    preferences: { ...s.preferences, ...prefs },
  }));
}

// ─── User Profile ──────────────────────────────────────────────────────

export function getCurrentUserProfile() {
  if (!_hydrated || !_state) return null;
  return _state.userProfiles[0] ?? null;
}

export function updateUserProfile(updates: Partial<DemoState['userProfiles'][0]>): void {
  updateDemoState((s) => ({
    ...s,
    userProfiles: s.userProfiles.map((u, idx) =>
      idx === 0 ? { ...u, ...updates } : u
    ),
  }));
}

export function getOrganizationMemberships(userId?: string) {
  if (!_hydrated || !_state) return [];
  if (!userId) return _state.organizationMemberships;
  return _state.organizationMemberships.filter((m) => m.userId === userId);
}

export function updateOrganizationMembership(
  id: string,
  updates: Partial<DemoState['organizationMemberships'][0]>
): void {
  updateDemoState((s) => ({
    ...s,
    organizationMemberships: s.organizationMemberships.map((m) =>
      m.id === id ? { ...m, ...updates } : m
    ),
  }));
}

export function getWorkspaceMemberships(userId?: string) {
  if (!_hydrated || !_state) return [];
  if (!userId) return _state.workspaceMemberships;
  return _state.workspaceMemberships.filter((m) => m.userId === userId);
}

export function updateWorkspaceMembership(
  id: string,
  updates: Partial<DemoState['workspaceMemberships'][0]>
): void {
  updateDemoState((s) => ({
    ...s,
    workspaceMemberships: s.workspaceMemberships.map((m) =>
      m.id === id ? { ...m, ...updates } : m
    ),
  }));
}

// ─── Workspaces ────────────────────────────────────────────────────────

export function getWorkspaces(): DemoWorkspace[] {
  if (!_hydrated || !_state) return [];
  return _state.workspaces;
}

export function saveWorkspacesToStore(workspaces: DemoWorkspace[]): void {
  updateDemoState((s) => ({ ...s, workspaces }));
}

export function addWorkspaceToStore(workspace: DemoWorkspace): void {
  updateDemoState((s) => ({ ...s, workspaces: [...s.workspaces, workspace] }));
}

// ─── OrganizationMunicipalityConfigs (persistent) ─────────────────────

export function getOrganizationMunicipalityConfigs(organizationId?: string): import('./schema').DemoOrgMunicipalityConfig[] {
  if (!_hydrated || !_state) return [];
  if (!organizationId) return _state.organizationMunicipalityConfigs;
  return _state.organizationMunicipalityConfigs.filter((c) => c.organizationId === organizationId);
}

export function addOrganizationMunicipalityConfig(config: import('./schema').DemoOrgMunicipalityConfig): void {
  updateDemoState((s) => {
    // Prevent duplicates: same organizationId + municipalityId
    const exists = s.organizationMunicipalityConfigs.some(
      (c) => c.organizationId === config.organizationId && c.municipalityId === config.municipalityId
    );
    if (exists) return s;
    return {
      ...s,
      organizationMunicipalityConfigs: [...s.organizationMunicipalityConfigs, config],
    };
  });
}

export function updateOrganizationMunicipalityConfig(
  id: string,
  updates: Partial<import('./schema').DemoOrgMunicipalityConfig>
): void {
  updateDemoState((s) => ({
    ...s,
    organizationMunicipalityConfigs: s.organizationMunicipalityConfigs.map((c) =>
      c.id === id ? { ...c, ...updates } : c
    ),
  }));
}

export function removeOrganizationMunicipalityConfig(id: string): void {
  updateDemoState((s) => ({
    ...s,
    organizationMunicipalityConfigs: s.organizationMunicipalityConfigs.filter((c) => c.id !== id),
  }));
}

// ─── Trash / Restore / Permanent Delete for OrgMunicipalityConfig ─────

/** Soft-delete: sets trashedAt/trashedBy. Municipality disappears from active sections. */
export function trashMunicipalityConfig(id: string, trashedBy = 'demo-user'): void {
  updateDemoState((s) => ({
    ...s,
    organizationMunicipalityConfigs: s.organizationMunicipalityConfigs.map((c) =>
      c.id === id
        ? { ...c, trashedAt: new Date().toISOString(), trashedBy }
        : c
    ),
  }));
}

/** Restore: clears trashedAt/trashedBy. Municipality returns to its politicalStatus section. */
export function restoreMunicipalityConfig(id: string): void {
  updateDemoState((s) => ({
    ...s,
    organizationMunicipalityConfigs: s.organizationMunicipalityConfigs.map((c) =>
      c.id === id
        ? { ...c, trashedAt: null, trashedBy: null }
        : c
    ),
  }));
}

/**
 * Permanent delete: removes config entirely.
 * ONLY call after verifying no workspace or operational data exists.
 */
export function permanentDeleteMunicipalityConfig(id: string): void {
  updateDemoState((s) => ({
    ...s,
    organizationMunicipalityConfigs: s.organizationMunicipalityConfigs.filter((c) => c.id !== id),
  }));
}

/**
 * Returns true if a municipality has an associated workspace or operational data
 * scoped to the given organization.
 *
 * IMPORTANT: Only workspaces matching BOTH organizationId AND municipalityId are
 * considered. A workspace belonging to a different organization for the same
 * municipality must NOT block deletion of this organization's config.
 *
 * Used to guard permanent delete of OrganizationMunicipalityConfig.
 */
export function municipalityHasOperationalData(
  municipalityId: string,
  organizationId: string,
  workspaceId?: string | null
): boolean {
  if (!_hydrated || !_state) return false;

  // Collect workspace IDs that belong to this specific organization + municipality.
  // A workspace from a different organization must never block this delete.
  const orgWorkspaces = _state.workspaces.filter(
    (w) => w.organizationId === organizationId && w.municipalityId === municipalityId
  );

  // If a specific workspaceId is provided, verify it belongs to this org+municipality.
  if (workspaceId) {
    const ownedWs = orgWorkspaces.find((w) => w.id === workspaceId);
    if (ownedWs) return true;
  }

  // Check any workspace for this org + municipality combination.
  if (orgWorkspaces.length > 0) return true;

  // Check operational data scoped to the org-owned workspace IDs.
  const orgWsIds = new Set(orgWorkspaces.map((w) => w.id));

  const hasSessions = _state.plenarySessions.some(
    (s) => s.workspaceId != null && orgWsIds.has(s.workspaceId)
  );
  if (hasSessions) return true;

  const hasIssues = _state.issues.some(
    (i) => i.workspaceId != null && orgWsIds.has(i.workspaceId)
  );
  if (hasIssues) return true;

  const hasDrafts = _state.drafts.some(
    (d) => d.workspaceId != null && orgWsIds.has(d.workspaceId)
  );
  if (hasDrafts) return true;

  const hasAgendaItems = _state.agendaItems.some(
    (a) => a.workspaceId != null && orgWsIds.has(a.workspaceId)
  );
  if (hasAgendaItems) return true;

  const hasDocuments = _state.documents.some(
    (d) => d.workspaceId != null && orgWsIds.has(d.workspaceId)
  );
  if (hasDocuments) return true;

  const hasSuggestedQuestions = _state.suggestedQuestions.some(
    (q) => q.workspaceId != null && orgWsIds.has(q.workspaceId)
  );
  if (hasSuggestedQuestions) return true;

  const hasActivityEvents = _state.activityEvents.some(
    (e) => e.workspaceId != null && orgWsIds.has(e.workspaceId)
  );
  if (hasActivityEvents) return true;

  const hasAlerts = _state.alerts.some(
    (a) => a.workspaceId != null && orgWsIds.has(a.workspaceId)
  );
  if (hasAlerts) return true;

  const hasNotifications = _state.notifications.some(
    (n) => n.workspaceId != null && orgWsIds.has(n.workspaceId)
  );
  return hasNotifications;
}

// ─── Workspace-scoped getters ──────────────────────────────────────────

/** Returns plenary sessions scoped to a workspace. Falls back to municipalityId if no workspaceId. */
export function getSessionsByWorkspace(workspaceId: string): PlenarySession[] {
  if (!_hydrated || !_state) return [];
  return _state.plenarySessions.filter((s) =>
    s.workspaceId ? s.workspaceId === workspaceId : false
  );
}

/** Returns issues scoped to a workspace. Falls back to municipalityId filter if workspaceId absent. */
export function getIssuesByWorkspace(workspaceId: string): import('./schema').DemoIssue[] {
  if (!_hydrated || !_state) return [];
  return _state.issues.filter((i) =>
    i.workspaceId ? i.workspaceId === workspaceId : false
  );
}

/** Returns drafts scoped to a workspace. */
export function getDraftsByWorkspace(workspaceId: string): Draft[] {
  if (!_hydrated || !_state) return [];
  return _state.drafts.filter((d) =>
    d.workspaceId ? d.workspaceId === workspaceId : false
  );
}

// ─── Activity Events ───────────────────────────────────────────────────

export function addActivityEvent(event: Omit<import('@/types/models').ActivityEvent, 'id'>): void {
  const newEvent: import('@/types/models').ActivityEvent = {
    ...event,
    id: `activity-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  };
  updateDemoState((s) => ({
    ...s,
    activityEvents: [newEvent, ...s.activityEvents].slice(0, 200),
  }));
}

// ─── Legacy resetDemoData (backward compat) ────────────────────────────

/**
 * @deprecated Use resetDemoState() instead.
 */
export function resetDemoData(): void {
  resetDemoState();
}
